/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import {
	VALIDATOR_DRAG_TOO_FAR_AWAY,
	ACTIVE_LINE_MODIFIED_DURING_DRAG,
	PINNED_TICKETS_DRAGGED_TO_ACTIVE,
	DRAG_ATTACHE_INTO_FLOATING,
} from '@touchplan/strings';
import { makeStack } from 'utils/basic-utils';
import { snapping } from 'utils/ticket-utils';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { fbReporting } from 'src/app/plain-javascript/background-scripts/fb-reporting';
import {
	ActualPhase,
	dateCache,
	guessTimeOffsetFromLeft,
	PLAN_CONSTANTS,
	TicketScale,
} from '@touchplan/common';

import { Point, Rectangle } from 'pixi.js-legacy';
import { default as PaperPoint } from '@appLegacy/lib/math/Point';

import { Side, Ticket, TicketRenderTypes, TicketFields, applyVarianceToPin } from '@touchplan/common';
import { Plan } from '@touchplan/common';
import { DragAtEdge, EdgeDistance } from 'src/app/app-data/models/DragAtEdge';
import { PullCalculation } from 'src/app/app-data/models/PullCalculation';
import { TicketSelection } from 'src/app/app-data/models/TicketSelection';
import { CONSTRAINT_ACCEPTANCE_STATE } from '@touchplan/common';
import { ILoggerService } from '@touchplan/logging';

import {
	FancyActionTypes,
	FancyTicketAction,
	TicketActionTypes,
} from 'src/app/app-data/actions/ticket-actions';

import { PlanState } from 'src/app/services/plan-state.service';
import {
	ActiveColumnService,
	DatePositionValidator,
} from 'src/app/services/legacy-helpers/ajs-upgrade-providers';

import { ForEachAble } from 'src/app/app-data/interfaces';
import { ProjectMember } from '@touchplan/common';
import { TicketType } from '@touchplan/common';
import { ListLike } from 'src/app/app-data/interfaces/list-like';
import { hasAttachment } from 'src/app/plain-javascript/services/HasAttachment';

import * as strings from '@touchplan/strings';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { LEVEL_OF_DETAIL_TABLE, LevelOfDetail } from '@appPlanApplication/lod/LevelOfDetail';
import { GranularEnabledService } from 'src/app/common/granular-time/granular-enabled.service';
import { fancyDatabase } from '@touchplan/client-firebase';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);

declare const fancyNotification;

export interface DragAnalysis {
	hasPromises: boolean;
	promiseList?: Ticket[];
	hasConstraints: boolean;
	constraintList?: Ticket[];
	hasCompleted: boolean;
	completedList?: Ticket[];
	hasStarted: boolean;
	startedList?: Ticket[];
	hasMovedHorizontally: boolean;
	movedHorizontallyList?: Ticket[];
	isOutofBounds: boolean;
	isAlmostOutofBounds: boolean;
	isInAbyss: boolean;
	needVariance: boolean;
	needVarianceList?: Ticket[];
	clearVariance: boolean;
	clearVarianceList?: Ticket[];
	hasDependencies: boolean;
	dependencyList?: Ticket[];
	hasAttachments?: Promise<boolean>; //temp
	preservedIdConflicts: Set<string> | null;
}

export enum DragPromptConditions {
	'DEBUG',
	'DONT',
	'ALMOSTOUTOFBOUNDS',
	'COLLECTVARIANCE',
	'FLOATCONSTRAINTS',
	'FLOATDEPENDENCIES',
	'HASATTACHMENTS', //temp
	'DUPLICATE_TICKET_IDS',
}
export interface DragPromptOutput {
	type: DragPromptConditions;
	payload: any;
}

interface StartData {
	screenX: number;
	screenY: number;
	x: number;
	y: number;
	z: number;
	originSide: Side;
	hasPins: boolean;
	hasLoggedPullPinWarning: boolean;
}

interface IAngularLand {
	activeColumnService: ActiveColumnService;
	datePositionValidator: DatePositionValidator;
	granularEnabledService: GranularEnabledService;
}

export interface DragConfiguration {
	dragAtEdgeDistance?: EdgeDistance;
	/** an override to force the drag mode to be vertical only (default for completed and mixed pull/active tickets) */
	forceVerticalOnly?: boolean;
	/** currently unused, an override to force the origin side of the drag. Will likely need for more accurate output of pull only
	 * and active only pastes
	 */
	forceOriginSide?: Side;
}

// declare global{
// 	namespace Ticket.view{
// 		export interface DragStartData{
// 			x: number
// 		}
// 	}
// }
//
// namespace Ticket.view.dragStartData{
// 	export let x: number;
// }
// PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH

const USE_NEW_GRID_DEFAULT = true;

const pullTicketWidth = 290;
const pullTicketHeight = 270;
const activeColumnWidth = PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH;
const activeColumnHeight = PLAN_CONSTANTS.ACTIVE_COLUMN_HEIGHT;
const snappingDistance = 50;
const stickiness = 40;
const activeLineWidth = 60;
const pullSnapGridSize = 340;

window.stickiness = stickiness;

export class TicketDragService {
	public dragInProgress = false;
	public dragCanceled = false;

	public showVariancePrompt = new BehaviorSubject(false);

	private planData: Plan;
	private hasActiveLine = false;
	public dragAtEdgeCancel = new Subject();
	private dragAtEdge: DragAtEdge;
	private hasCrossedActiveLine = false;
	private gtpEnabled: boolean;

	private fixedVertical = false;

	private startRect?: Rectangle;
	private activeSortedTickets: Ticket[];
	private dragConfiguration: DragConfiguration = {};

	private calculatedPullColumns: PullCalculation;

	private tickets: Map<string, Ticket>;

	public ghostTickets: Subject<any> = new Subject();
	private internalGhostTickets: Map<string, any> = new Map();

	private startData: StartData;

	private debugRect: Rectangle;
	private debugRectId: string = null;

	private unsubscribes = [];

	private snapCache = {
		xPull: new Set(),
		yPull: new Set(),
		yActive: new Set(),
	};

	//temp floating space attachment blocker
	private hasAttachments?: Promise<boolean>;
	private logger: ILoggerService;

	constructor(
		private planState: PlanState,
		private featureFlagManager: FeatureFlagManager,
		private angularLand: IAngularLand
	) {
		this.logger = LoggerServiceFactory.create('TicketDragService');
		planState.plan.object$.subscribe((plan) => {
			if (
				this.dragInProgress &&
				((!plan && this.planData) ||
					(plan && !this.planData) ||
					plan.view.activeLineDate !== this.planData.view.activeLineDate ||
					plan.view.activeLineX !== this.planData.view.activeLineX)
			) {
				Logging.warning(ACTIVE_LINE_MODIFIED_DURING_DRAG);
				this.dragCancel();
			}
			this.planData = plan;
			this.hasActiveLine = plan && plan.view.planHasActiveSpace;
		});
		this.ghostTickets = new Subject();
		// window.dragThing = this;
		// window.Rectangle = Rectangle;
	}

	//can pass in planState.tickets.selectedTicket$ directly, or make your own if necessary
	public dragStart(
		center: Point,
		ticketSelection: Observable<TicketSelection>,
		dragConfiguration: DragConfiguration = {}
	) {
		//just used by the reporting
		let lastDragCanceled = this.dragCanceled;
		let lastDragInProgress = this.dragInProgress;

		if (!this.planData) {
			throw new Error('Plan data not loaded');
		}
		this.dragCanceled = false;
		this.dragInProgress = true;
		this.hasCrossedActiveLine = false;
		this.dragConfiguration = dragConfiguration;
		// cache if GTP is enabled at start of drag
		this.gtpEnabled = this.isGTPEnabled();

		this.unsubscribes = [];
		// this.logger.log('start center', center);

		this.debugRect = new Rectangle(0, 0, 50, 900);
		// this.debugRectId = this.planState.debugRectangles.addRectangle(this.debugRect);

		var camera = this.planState.canvasConnector.camera;

		var selectionUnSub = ticketSelection.subscribe((tickets) => this.ticketSelectionModfied(tickets));
		// this.logger.log('selectionUnSub', selectionUnSub);
		this.unsubscribes.push(selectionUnSub);
		//shockingly, this.tickets _will_ be defined now, _yay observables_

		if (!this.tickets || !this.tickets.size) {
			fbReporting.reportRealWarning(
				'tickets undefined in dragStart',
				Object.assign(makeStack(new Error('tickets undefined in dragStart')), {
					dragCanceled: lastDragCanceled,
					dragInProgress: lastDragInProgress,
					planStateState: this.planState.stateSync,
					entireTicketListCount:
						this.planState.tickets && this.planState.tickets.syncList
							? this.planState.tickets.syncList.size
							: 0,
					selectedTicketsCount:
						this.planState.tickets && this.planState.tickets.selectedTicket$
							? this.planState.tickets.selectedTicket$.getValue().list.size
							: 0,
				})
			);
			return;
		}
		if (!this.showVariancePrompt.getValue()) {
			this.planState.actions.changeTicketViewToDrag(this.tickets, (dragConfiguration as any).isUndoable);
		}

		this.startData = this.calcStartData(center, this.tickets);

		//placeholder
		const windowRect = new Rectangle(0, 0, window.innerWidth, window.innerHeight);

		//move this to a function
		this.dragAtEdge = new DragAtEdge(windowRect, dragConfiguration.dragAtEdgeDistance);
		this.unsubscribes.push(
			this.dragAtEdge.change$.pipe(takeUntil(this.dragAtEdgeCancel)).subscribe((map) => {
				var revisedPoint = map.shiftedPoint;
				let z = camera.getScale().x;
				// debugger;
				this.planState.scopeSoup.hud.onTranslate(new PaperPoint(-map.deltaX, -map.deltaY));
				// this.logger.log('bleh', this.startRect.left, revisedPoint.x/z, this.startRect.left + revisedPoint.x/z, this.planData.activeLineX);
				if (
					this.hasActiveLine &&
					this.startData.hasPins &&
					this.startRect.left + revisedPoint.x / z >= this.planData.view.activeLineX
				) {
					if (!this.startData.hasLoggedPullPinWarning) {
						Logging.notice(PINNED_TICKETS_DRAGGED_TO_ACTIVE);
						this.startData.hasLoggedPullPinWarning = true;
					}
					return;
				}

				this.tickets.forEach((t, idx) => {
					this.planState.actions.updateTicketLiveData(t.$id, {
						left: t.view.left + map.deltaX / z,
						top: t.view.top + map.deltaY / z,
					});
				});
			})
		);
		this.recalcStart();

		if (this.showVariancePrompt.getValue()) {
			this.showVariancePrompt.next(false);
		}

		//temp floating space attachment blocker
		const attachPaths = hasAttachment.getPathsForTicketsOnSamePlan(
			this.planState.projectId,
			this.planState.planId,
			Array.from(this.tickets.keys())
		);
		this.hasAttachments = hasAttachment.hasAnyAttachments(attachPaths, this.planState.projectId);
	}

	//called when a ticket gets added/ removed from the collection mid drag
	private recalcStart() {
		if (!this.tickets.size) {
			this.logger.log('abort');

			this.dragCancel();
			return;
		}
		// this.logger.log('this.ticket.size', this.tickets.size);
		this.tickets.forEach((t: Ticket) => {
			//leave it if it's already there (handles updates, probably doesn't harm anything)
			if (t.view.dragStartData) {
				return;
			}

			if (t.renderType !== TicketRenderTypes.drag) {
				this.planState.actions.changeTicketViewToDrag(t);
			}

			//SPECIAL OVERRIDE
			this.planState.actions.updateTicketLiveData(t.$id, { pauseVarianceRemoval: true });

			t.view.dragStartData = {
				x: t.view.left,
				y: t.view.top,
				width: t.view.width,
				height: t.view.height,
				start: t.view.liveStart,
				finish: t.view.liveFinish,
			};

			//this will only get set during the drag, it will not clear
			if (t.view.liveLastPromise) {
				this.startData.hasPins = true;
			}
		});

		this.startRect = calculateStartRect(this.tickets);
		if (this.startData.originSide === Side.ACTIVE) {
			this.calculatedPullColumns = this.calculatePullFromActiveRect(this.tickets);
			var startDate = this.angularLand.activeColumnService.guessDateFromX(
				this.startRect.left,
				-activeColumnWidth
			);
			this.tickets.forEach((t) => {
				var leftEdgeDiff = Math.round((t.view.dragStartData.x - this.startRect.left) / activeColumnWidth);
				t.view.dragStartData.startDayOffset = leftEdgeDiff;

				var workdays = this.planState.calendarWorkdays.getWorkdaysWithinRange(
					startDate,
					leftEdgeDiff,
					t.view.liveRole ? t.view.liveRole.$id : null,
					true //leftEdgeDiff === 0
				);
				t.view.dragStartData.startWorkDayOffset = workdays;
				// this.logger.log(workdays, t.view.dragStartData);
			});

			this.activeSortedTickets = sortActiveTickets(this.tickets);

			//column limited yOrder
			var maxYOrder = 0;
			this.calculatedPullColumns.regions.forEach((region) => {
				var ySorted = [...region.tickets];
				ySorted.sort((a, b) => {
					var aS = a.view.dragStartData;
					var bS = b.view.dragStartData;
					if (aS.y === bS.y) {
						if (aS.x + aS.width === bS.x + bS.width) {
							return a.$id.localeCompare(b.$id);
						}
						return aS.x + aS.width - (bS.x + bS.width);
					}
					return aS.y - bS.y;
				});
				ySorted.forEach((wt, key) => {
					wt.view.dragStartData.yOrder = key;
					if (key > maxYOrder) {
						maxYOrder = key;
					}
				});
			});
			this.calculatedPullColumns.pullStartRect.height =
				PLAN_CONSTANTS.PULL_TICKET_HEIGHT +
				(PLAN_CONSTANTS.PULL_TICKET_HEIGHT + snappingDistance) * maxYOrder;
		}

		//currently this is the only place where this value can be initialized per drag as false
		this.fixedVertical = !!this.dragConfiguration.forceVerticalOnly;
		//check for fixed vertical
		// mixed side selection/ completed
		// don't bother iterating tickets when it's forced
		if (!this.fixedVertical) {
			let lastSide: Side;
			this.tickets.forEach((ticket) => {
				if (ticket.view.actualPhase === ActualPhase.started) {
					this.fixedVertical = true;
				}
				if (ticket.view.isActualized) {
					this.fixedVertical = true;
				}
				if (lastSide !== undefined && lastSide !== ticket.view.side) {
					this.fixedVertical = true;
				}
				lastSide = ticket.view.side;
			});
		}
	}

	private isGTPEnabled(): boolean {
		return this.angularLand.granularEnabledService.granularScaleSync === TicketScale.Hourly;
		// return (
		// 	(this.featureFlagManager.getFlag(DynamicFlagTypes.FBDB_GTP_ENABLED) ||
		// 		this.featureFlagManager.getFlag(
		// 			DynamicFlagTypes.FBDB_GTP_ENABLED_BY_PROJECT,
		// 			this.planState.projectId
		// 		)) &&
		// 	// Note that zoom can be changed during drag, so this is cheating a bit
		// 	LevelOfDetail.getLODLevel() >= LEVEL_OF_DETAIL_TABLE.GTP_MAXIMUM
		// );
	}

	public drag(center: Point) {
		if (this.dragCanceled) {
			return true;
		}
		this.dragAtEdge.update(center);
		var camera = this.planState.canvasConnector.camera;

		if (!this.tickets || !this.tickets.size) {
			fbReporting.reportRealWarning(
				'tickets undefined in drag',
				Object.assign(makeStack(new Error('tickets undefined in drag')), {
					dragCanceled: this.dragCanceled,
					dragInProgress: this.dragInProgress,
					center: center,
					planStateState: this.planState.stateSync,
					entireTicketListCount:
						this.planState.tickets && this.planState.tickets.syncList
							? this.planState.tickets.syncList.size
							: 0,
					selectedTicketsCount:
						this.planState.tickets && this.planState.tickets.selectedTicket$
							? this.planState.tickets.selectedTicket$.getValue().list.size
							: 0,
				})
			);
			return;
		}

		var hide = false;
		this.tickets.forEach((t) => (hide = hide || t.hidden));
		if (hide) {
			this.hideGhostTickets();
			return;
		}

		var worldPoint = new Point(camera.screenToRealX(center.x), camera.screenToRealY(center.y));
		var delta = new Point(worldPoint.x - this.startData.x, worldPoint.y - this.startData.y);

		var drawSpecial = false;

		var liveRect = this.startRect.clone();
		liveRect.x += delta.x;
		liveRect.y += delta.y;

		var lockInPlaceWhenInSameColumn =
			this.startData.originSide === Side.ACTIVE &&
			liveRect.x > this.startRect.x - activeColumnWidth / 2 &&
			liveRect.x < this.startRect.x + activeColumnWidth / 2 &&
			// do not lock in place when GTP is enabled
			!this.gtpEnabled;

		// if (window.MOC4282) {
		// 	this.logger.log("MOC-4282 earlier data", this.startData.originSide, liveRect.x);
		// }

		// this.logger.log('liveRect', liveRect.x, lockInPlace);
		// this.logger.log('start side', this.startData.originSide);
		if (this.fixedVertical || lockInPlaceWhenInSameColumn) {
			var fixedVerticalSnaps = this.buildSnapCache(this.startRect);
			var x = 0;
			var c = this.startData.originSide === Side.PULL ? fixedVerticalSnaps.yPull : fixedVerticalSnaps.yActive;
			var y = this.findSnaps(liveRect.y, c) - this.startRect.y;
		}
		//starting on pull side
		else if (this.startData.originSide === Side.PULL) {
			this.snapCache = this.buildSnapCache(this.startRect);
			if (liveRect.x < this.planData.view.activeLineX + activeLineWidth) {
				// this.showGhostTickets();
				var calcedActive = this.calculateActiveFromPullRect(this.tickets, delta);

				// this.debugRect.x = calcedActive.activeRect.x;
				// this.debugRect.y = liveRect.y;
				// this.debugRect.width = calcedActive.activeRect.width;
				// this.planState.debugRectangles.updateRectangle(this.debugRectId, this.debugRect);

				var calcedActiveWriteData = this.generateActiveWriteData(calcedActive, delta);

				//whole thing is in active space
				if (Math.round(calcedActive.activeRect.right) <= this.planData.view.activeLineX) {
					drawSpecial = true;
					this.hideGhostTickets();
					calcedActiveWriteData.forEach((ticket) => {
						this.planState.actions.updateTicketLiveData(ticket.$id, ticket.write);
					});
					this.hasCrossedActiveLine = true;
				}
				//ghosty things
				else {
					this.showGhostTickets();
					// this.logger.log('show ghost tickets');
					calcedActiveWriteData.forEach((ticket) => {
						var ghost = this.internalGhostTickets.get(ticket.$id);
						Object.assign(ghost.view, ticket.write);
						this.planState.actions.updateTicketLiveData(ticket.$id, ticket);
						var action = new FancyTicketAction(ghost, TicketActionTypes.ticketLiveUpdate, ghost.$id);
						this.ghostTickets.next(action);
					});

					var x = this.findSnaps(liveRect.x, this.snapCache.xPull) - this.startRect.x;
					var y = this.findSnaps(liveRect.y, this.snapCache.yPull) - this.startRect.y;
					//cap the delta
					x = this.planData.view.activeLineX - this.startRect.x + activeLineWidth;
					this.hasCrossedActiveLine = false;
				}
			} else {
				let useOldGrid = !USE_NEW_GRID_DEFAULT;
				if (
					this.planData.view.staticPullGridOverride === false ||
					this.planData.view.staticPullGridOverride === true
				) {
					useOldGrid = !this.planData.view.staticPullGridOverride;
				}
				// if(keyModifier.isAppleSafeCtrl){ useOldGrid = true; }

				if (useOldGrid) {
					var x = this.findSnaps(liveRect.x, this.snapCache.xPull) - this.startRect.x;
					// var x = liveRect.x - this.startRect.x;
				} else {
					// var x = this.findSnaps(liveRect.x, this.snapCache.xPull) - this.startRect.x;
					var x = liveRect.x - (liveRect.x % pullSnapGridSize) - this.startRect.x;
				}
				var y = this.findSnaps(liveRect.y, this.snapCache.yPull) - this.startRect.y;
				this.hideGhostTickets();
				this.hasCrossedActiveLine = false;
			}
		} else if (this.startData.originSide === Side.ACTIVE) {
			// this.logger.log('is starting active');
			drawSpecial = true;
			// disable snapping to day columns for gtp
			if (!this.gtpEnabled) {
				var activeColumns = this.angularLand.activeColumnService.columns();
				var match = snapping(liveRect.x, activeColumns, activeColumnWidth, (listItem) => {
					return listItem.origStartX;
				});
				if (match !== false) {
					liveRect.x = match.loc;
				}
			}

			//liveRect is now modified, along with tickets
			var shouldGhost = this.figureOutActive(liveRect, this.activeSortedTickets);
			if (shouldGhost && this.startData.hasPins && !this.startData.hasLoggedPullPinWarning) {
				Logging.notice(PINNED_TICKETS_DRAGGED_TO_ACTIVE);
				this.startData.hasLoggedPullPinWarning = true;
			}
			if (this.startData.hasPins) {
				shouldGhost = false;
			}
			// this.logger.log('hasPins', this.startData.hasPins, shouldGhost);
			var pullLeft =
				this.startRect.left +
				delta.x +
				this.calculatedPullColumns.regions[this.calculatedPullColumns.regions.length - 1].startX;

			var activeSnaps = this.buildSnapCache(liveRect);

			var activeSnappedY = this.findSnaps(liveRect.y, activeSnaps.yActive) - this.startRect.y;

			var pullRect = this.calculatedPullColumns.pullStartRect.clone();
			pullRect.x = this.startRect.x;
			pullRect.y = this.startRect.y;
			pullRect.x += delta.x;
			pullRect.y += delta.y;
			var pullFromActiveSnaps = this.buildSnapCache(pullRect);

			let useOldGrid = !USE_NEW_GRID_DEFAULT;
			if (
				this.planData.view.staticPullGridOverride === false ||
				this.planData.view.staticPullGridOverride === true
			) {
				useOldGrid = !this.planData.view.staticPullGridOverride;
			}
			// if(keyModifier.isAppleSafeCtrl){ useOldGrid = true; }

			if (useOldGrid) {
				var pullSnappedX = this.findSnaps(pullRect.x, pullFromActiveSnaps.xPull) - this.startRect.x;
			} else {
				var pullSnappedX = pullRect.x - (pullRect.x % pullSnapGridSize) - this.startRect.x;
			}
			var pullSnappedY = this.findSnaps(pullRect.y, pullFromActiveSnaps.yPull) - this.startRect.y;

			// this.planState.debugRectangles.updateRectangle(this.debugRectId, this.debugRect);

			var calculatedPullTicketData = [];
			// var snappedX = this.findSnaps(pullLeft, this.snapCache.xPull) - this.startRect.x;
			this.calculatedPullColumns.iterate((ticket, region) => {
				var obj = {
					left: Math.round(this.startRect.left + pullSnappedX + region.startX),
					top: Math.round(
						this.startRect.y +
							pullSnappedY +
							(PLAN_CONSTANTS.PULL_TICKET_HEIGHT + snappingDistance) * ticket.view.dragStartData.yOrder
					),
					width: PLAN_CONSTANTS.PULL_TICKET_WIDTH,
					height: PLAN_CONSTANTS.PULL_TICKET_HEIGHT,
					liveStart: ticket.view.dragStartData.start,
					liveFinish: ticket.view.dragStartData.finish,
					side: Side.PULL,
				};
				calculatedPullTicketData.push({ write: obj, $id: ticket.$id });
			});

			var calculatedActiveTicketData = [];
			this.tickets.forEach((t, idx) => {
				var left = Math.round(
					liveRect.x +
						t.view.lastWorkdayRep.shiftedStartOffset * activeColumnWidth +
						t.view.lastWorkdayRep.startOffsetDays * activeColumnWidth
				);
				// var left = Math.round(liveRect.x + t.view.dragStartData.startDayOffset * activeColumnWidth + t.view.lastWorkdayRep.startOffsetDays * activeColumnWidth);
				var width = Math.round(t.view.lastWorkdayRep.calendarDays * activeColumnWidth);
				var guessedStart = this.angularLand.activeColumnService.guessDateFromX(left, -activeColumnWidth);
				var guessedFinish = this.angularLand.activeColumnService.guessDateFromX(left + width);
				// this.logger.log('left', liveRect.x + t.view.dragStartData.startDayOffset * activeColumnWidth + t.view.lastWorkdayRep.startOffsetDays * activeColumnWidth, left, guessedStart, guessedFinish);
				var obj = {
					left: left,
					top: Math.round(t.view.dragStartData.y + activeSnappedY),
					width: width,
					height: Math.round(t.view.dragStartData.height),
					liveStart: guessedStart,
					liveFinish: guessedFinish,
				};
				calculatedActiveTicketData.push({ write: obj, $id: t.$id });
			});

			if (window.MOC4282) {
				this.logger.log('MOC-4282 data', {
					shouldGhost,
					calculatedActiveTicketData,
					liveRect,
					activeLineX: this.planData.view.activeLineX,
					hasCrossedActiveLine: this.hasCrossedActiveLine,
				});
			}

			if (shouldGhost) {
				// this.logger.log('should ghost');
				//calculated pullTickets
				if (pullLeft > this.planData.view.activeLineX + activeLineWidth) {
					// this.logger.log('pullify');
					this.hideGhostTickets();
					calculatedPullTicketData.forEach((ticket) => {
						this.planState.actions.updateTicketLiveData(ticket.$id, ticket.write);
					});
					this.hasCrossedActiveLine = true;
				}
				//active tickets and ghost pull tickets
				else {
					this.showGhostTickets();
					// this.logger.log('activate');
					calculatedActiveTicketData.forEach((ticket) => {
						this.planState.actions.updateTicketLiveData(ticket.$id, ticket.write);
					});

					calculatedPullTicketData.forEach((ticket) => {
						var ghost = this.internalGhostTickets.get(ticket.$id);
						Object.assign(ghost.view, ticket.write);
						var action = new FancyTicketAction(ghost, TicketActionTypes.ticketLiveUpdate, ghost.$id);
						this.ghostTickets.next(action);
					});
					this.hasCrossedActiveLine = false;
				}
			}
			//just active tickets
			else {
				this.hideGhostTickets();
				calculatedActiveTicketData.forEach((ticket) => {
					// this.logger.log('ticket', ticket.write.liveStart === ticket.write.liveFinish, ticket.write.liveStart, ticket.write.liveFinish);
					this.planState.actions.updateTicketLiveData(ticket.$id, ticket.write);
				});
				this.hasCrossedActiveLine = false;
			}
		} else {
			this.logger.log('da hell?');
		}

		//lazy short hand to prevent the default draw if some fancier is done
		if (drawSpecial) {
			return;
		}
		this.tickets.forEach((t) => {
			this.planState.actions.updateTicketLiveData(t.$id, {
				left: Math.round(t.view.dragStartData.x + x),
				top: Math.round(t.view.dragStartData.y + y),
				width: Math.round(t.view.dragStartData.width),
				height: Math.round(t.view.dragStartData.height),
				liveStart: t.view.dragStartData.start,
				liveFinish: t.view.dragStartData.finish,
			});
		});
	}

	public dragEnd(center: Point) {
		if (this.tickets) {
			//ignore floating space only drags
			this.finalUpdateBeforePersist();

			let anyNeedVariance = false;
			this.tickets.forEach((t) => (anyNeedVariance = t.view.needsVariance || anyNeedVariance));

			if (!anyNeedVariance) {
				// this.applyConstraintAcceptance();
				// analyticsEventsService.movedTickets(this.tickets.size, this.planState.planId, false, false, false, this.hasCrossedActiveLine);
				// this.planState.actions.persistTicketData(this.tickets);
				// this.planState.actions.changeTicketViewToDefault(this.tickets);
				this.saveTickets(this.tickets);
			} else {
				this.logger.log('NEEED VARIANCE');
				this.showVariancePrompt.next(true);
				this.planState.canvasConnector.camera.focusOnTickets(this.tickets);
			}
		}
		this.dragCleanUp();
	}

	/** Remove a variance flag and recalc some calendar workdays stuff */
	public finalUpdateBeforePersist() {
		if (this.tickets) {
			//ignore floating space only drags
			this.tickets.forEach((t) => {
				const updateData: TicketFields = {
					//REMOVE SPECIAL VARIANCE CALC OVERRIDE
					pauseVarianceRemoval: false,
				};
				//only recalc workdays and duration when drag ends on the active side, and the ticket is not actualized
				if (t.view.side === Side.ACTIVE && !t.view.isActualized) {
					// TP-626 temp workaround
					// Search around the app for that issue number for the rest of these, the intent is to correct them when
					// unhooking the dependency of "ticket needs to be part of core ticket list to render in the plan"
					// which is part of the intended design for the floating tickets overhaul
					const exceptionDays = new Map(t.view.ticketExceptionDaysPrevPlan || t.view.ticketExceptionDays);

					// Commenting this for now, now the getMetadataForDuration function will handle the exception days
					// Including setting the start date to true
					// exceptionDays.set(t.view.liveStart, true);
					const maths = this.planState.calendarWorkdays.getMetadataForDuration(
						exceptionDays,
						t.view.roleId,
						t.view.liveStart,
						t.view.liveDurationDays
					);
					updateData.liveWorkingDays = maths.liveWorkingDays;
					updateData.liveFinish = maths.liveFinish;
					updateData.width = maths.width;

					// TP-626 temp workaround
					if (t.view.ticketExceptionDaysPrevPlan) {
						t.view.ticketExceptionDaysPrevPlan = null;
					}
				}

				this.planState.actions.updateTicketLiveData(t.$id, updateData);
			});
		}
	}

	public updateDragAtEdgeDistance(side: string, value: number) {
		this.dragAtEdge.updateDragAtEdgeDistance(side, value);
	}

	//actually save tickets after a successful drag
	private saveTickets(tickets: Map<string, Ticket>) {
		this.applyConstraintAcceptance(tickets);
		this.applyStartTimeOffset(tickets);
		analyticsEventsService.movedTickets(
			tickets.size,
			this.planState.planId,
			false,
			false,
			false,
			this.hasCrossedActiveLine
		);
		this.planState.actions.persistTicketData(tickets);
		this.planState.actions.changeTicketViewToDefault(tickets);
	}

	private applyStartTimeOffset(tickets: Map<string, Ticket>) {
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7302
		// This is to temporarily store the start time offset on the ticket. Eventually it will live elsewhere.
		// see https://mocasystems.atlassian.net/wiki/spaces/DI/pages/3458957313/Frontend+Granular+Planning#ticketWorkDays-(not-stored-in-the-tickets-node)

		//assume valid
		function parseDuration(duration: string) {
			const s = duration.split(':');
			return dayjs.duration({
				hours: parseInt(s[0]),
				minutes: parseInt(s[1]),
				seconds: parseInt(s[2]),
			});
		}
		//some of the ways this is bad/ incomplete
		// - overwrites all exceptions
		// - hopes a duration will be there in the workdays
		// - bakes a derived duration into an exception
		// - overly aggressive aborts
		// - etc
		if (!this.isGTPEnabled()) {
			return;
		}
		tickets.forEach((t) => {
			// scope very narrowly
			if (!t.view.left || !t.view.liveStart || t.view.isActualized || !t.view.liveWorkingPeriods?.length) {
				return;
			}

			const path = fancyFirebaseGetters.getTicketWorkDaysPath(
				this.planState.projectId!,
				this.planState.planId,
				t.$id
			);
			const workDaysRef = fancyFirebaseReferencer.getRef(path, this.planState.projectId);

			//good ole "off by one"
			// do a poor job patching it here
			const realStartDate = dateCache.addDays(t.view.liveStart, -1);
			const startTime = guessTimeOffsetFromLeft(t.view.left, this.planData.view.activeLineX);
			let baseDuration = t.view.liveWorkingPeriods[0].duration;
			//"HH:mm:ss"

			//horrible "made on on the spot date math thing to work around >24 durations"
			const dayjsStart = parseDuration(startTime);
			const dayjsDuration = parseDuration(baseDuration!);

			const added = dayjsStart.add(dayjsDuration);
			if (added.days() >= 1) {
				baseDuration = dayjs.duration({ days: 1 }).subtract(dayjsStart).format('HH:mm:ss');
				// baseDuration = added.format("HH:mm:ss");
			}

			const workDaysObject = {
				[realStartDate]: {
					work: true,
					workingPeriod: {
						start: startTime,
						duration: baseDuration,
					},
				},
			};

			//give it an arbitrary timeout to avoid the old version
			setTimeout(() => {
				console.log('applying workdays to', path, 'of', workDaysObject);
				fancyDatabase.set(workDaysRef, workDaysObject);
			}, 300);

			// this.planState.actions.updateTicketLiveData(t.$id, {
			// 	startTimeOffset: guessTimeOffsetFromLeft(t.view.left, this.planData.view.activeLineX),
			// });
			// console.log('startTimeOffset', guessTimeOffsetFromLeft(t.view.left, this.planData.view.activeLineX));
			// END PLACEHOLDER REPLACE AS PART OF TP-7302
		});
	}

	public applyConstraintAcceptance(ticketsOverride?: Map<string, Ticket>) {
		let tickets = ticketsOverride || this.tickets;
		let constraints = checkForConstraintAcceptance(this.planState.authedProjectMemberSync, tickets);
		constraints.forEach((t) => {
			this.planState.actions.updateTicketLiveData(t.$id, {
				assignedProjectMemberId: null,
				assignedProjectMemberRoleId: null,
				responsibleProjectMemberId: t.view.assignedProjectMemberId,
				responsibleProjectMemberRoleId: null,
			});
			let conName = t.view.activityName;
			fancyNotification.simpleMessage(
				'You accepted responsibility for "' + conName + '".',
				this.planState.scopeSoup
			);
		});
	}

	public dragCancel() {
		if (!this.dragInProgress) {
			return;
		}
		this.dragCanceled = true;
		this.planState.actions.changeTicketViewToDefault(this.tickets);
		this.dragCleanUp();
	}

	/** Turn off subscription listening so that the passed in selection can be modified as part of a more advanced dragEnd */
	public stopListening() {
		this.unsubscribes.forEach((unsub) => unsub.unsubscribe());
		this.unsubscribes = [];
	}

	//called to cleanup after a drag
	public dragCleanUp() {
		if (!this.dragInProgress) {
			return;
		}
		this.unsubscribes.forEach((unsub) => unsub.unsubscribe());
		this.unsubscribes = [];
		this.dragInProgress = false;
		this.planState.debugRectangles.removeRectangle(this.debugRectId);
		this.hideGhostTickets();

		//remove the state data (mostly just for the variance prompt drag restart thing)
		if (this.tickets) {
			this.tickets.forEach((t) => delete t.drag.dragStartData);
		}

		this.tickets = undefined;
		this.dragAtEdge.cancel();

		//temp attachment thing
		delete this.hasAttachments;
	}

	public destroy() {
		this.ghostTickets.complete();
		this.internalGhostTickets.clear();
		this.showVariancePrompt.next(false);
	}

	public analyzeDragStatus(
		droppedInFloating: boolean,
		mapOfPlanAndDraftTickets: Map<string, Ticket>,
		tickets?: ForEachAble<Ticket>
	): DragAnalysis {
		if (!tickets) {
			tickets = this.tickets;
		}
		if (!tickets) {
			tickets = [];
		} //likely this is a floating exclusive drag

		var preservedIdConflicts: Set<string> | null = null;
		if (!droppedInFloating) {
			preservedIdConflicts = this.preservedTicketIdConflicts(mapOfPlanAndDraftTickets);
		}

		var analysis: DragAnalysis = {
			hasPromises: false,
			hasConstraints: false,
			hasCompleted: false,
			hasStarted: false,
			hasDependencies: false,
			hasMovedHorizontally: false,
			isOutofBounds: false,
			isAlmostOutofBounds: false,
			isInAbyss: false,
			needVariance: false,
			clearVariance: false,
			hasAttachments: this.hasAttachments, //temp
			preservedIdConflicts: preservedIdConflicts,
		};
		analysis.dependencyList;
		function setCheck(hasKey, listKey, ticket) {
			analysis[hasKey] = true;
			if (!analysis[listKey]) {
				analysis[listKey] = [];
			}
			analysis[listKey].push(ticket);
		}

		tickets.forEach((ticket) => {
			var newLeft = ticket.view.left;
			var newTop = ticket.view.top;
			//promise check
			if (ticket.view.livePromisePeriodStack) {
				setCheck('hasPromises', 'promiseList', ticket);
			}

			//dependencies
			if (this.planState.dependencies.ticketHasDependencies(ticket.$id)) {
				setCheck('hasDependencies', 'dependencyList', ticket);
			}

			//constraint check
			if (ticket.view.type === TicketType.Constraint) {
				setCheck('hasConstraints', 'constraintList', ticket);
			}
			if (ticket.view.isActualized) {
				setCheck('hasCompleted', 'completedList', ticket);
			}
			if (ticket.view.actualPhase === ActualPhase.started) {
				setCheck('hasStarted', 'startedList', ticket);
			}
			if (ticket.view.dragStartData && Math.round(ticket.view.dragStartData.x) === Math.round(newLeft)) {
				setCheck('hasMovedHorizontally', 'movedHorizontallyList', ticket);
			}
			var validPosition = this.angularLand.datePositionValidator.isValidPosition(newLeft, newTop);
			var inWarningRange = this.angularLand.datePositionValidator.positionWarning(newLeft, newTop);
			if (!validPosition && !inWarningRange) {
				analysis.isOutofBounds = true;
			}
			if (!validPosition && inWarningRange) {
				analysis.isAlmostOutofBounds = true;
			}
			if (this.angularLand.datePositionValidator.isExtremelyInvalidPosition(newLeft, newTop)) {
				analysis.isInAbyss = true;
			}
		});
		// this.logger.log('analysis', analysis);
		return analysis;
	}

	public ticketsAreFromDraftsArea(): boolean {
		for (const ticket of this.tickets.values()) {
			if (!ticket.isAFloatingTicket) {
				return false;
			}
		}
		return true;
	}

	/**
	 * This function is intended to be called only in the case where tickets are dragged from drafts to a plan.
	 * It returns a set of those preserved ticket ID values which conflict in the drafts-to-plan move operation.
	 * If no conflicts exist, null is returned.
	 *
	 * Tickets can conflict in one of two ways:
	 *    1. Two or more tickets in the tickets being dragged from drafts have the same "preservedTicketId", or
	 *    2. A drafts area ticket has the same "preservedTicketId" as a ticket in the plan surface
	 *
	 * @param mapOfPlanAndDraftTickets a Map containing all of the plan surface tickets; it's OK if it also contains the drafts' tickets
	 * @returns a Set of the conflicting "preservedTicketId"s, or null if there are no conflicts
	 */
	public preservedTicketIdConflicts(mapOfPlanAndDraftTickets: Map<string, Ticket>): Set<string> | null {
		if (!this.ticketsAreFromDraftsArea()) {
			return null;
		}
		// This variable is named "draftTickets" because - as mentioned in the function comment -
		// "this function is intended to be called only in the case where tickets are dragged from drafts to a plan"
		const draftTickets = this.tickets;
		const conflictingIds = new Set<string>();
		const setOfDraftsPreservedTicketIds = new Set<string>();
		draftTickets.forEach((draftTicket) => {
			const preservedTicketId = draftTicket.view.preservedTicketId;
			if (preservedTicketId) {
				// Check #1:  Are there equal "preservedTicketId"s in the drafts tickets being moved?
				if (setOfDraftsPreservedTicketIds.has(preservedTicketId)) {
					// We've seen this already
					this.logger.warn(
						`Duplicate preservedTicketId\'s in the tickets being moved from Drafts:  preservedTicketId = ${preservedTicketId}`
					);
					conflictingIds.add(preservedTicketId);
				} else {
					setOfDraftsPreservedTicketIds.add(preservedTicketId);

					// Check #2:  Are there draft "preservedTicketId"s that equal plan ticket IDs?
					if (
						mapOfPlanAndDraftTickets.has(preservedTicketId) &&
						!mapOfPlanAndDraftTickets.get(preservedTicketId).isAFloatingTicket
					) {
						this.logger.warn(`Conflict of preservedTicketId and ticket ID:  id = ${preservedTicketId}`);
						conflictingIds.add(preservedTicketId);
					}
				}
			}
		});
		return conflictingIds.size === 0 ? null : conflictingIds;
	}

	public checkAbortConditions(analysis: DragAnalysis) {
		if (analysis.isInAbyss) {
			Logging.warning(VALIDATOR_DRAG_TOO_FAR_AWAY);
			//TODO - don't scopeSoup, probably not worth upgrading either, would likely be worthwhile to make a new one from scratch
			fbReporting.reportRealWarning('dragged into the abyss', {
				ticketCount: this.tickets.size,
				type: 'tickets',
				droppedInFloating: false,
			});
			return true;
		}
		if (analysis.isOutofBounds) {
			//TODO - don't scopeSoup, probably not worth upgrading either, would likely be worthwhile to make a new one from scratch
			analyticsEventsService.positionViolationBlocked();
			Logging.warning(VALIDATOR_DRAG_TOO_FAR_AWAY);
			return true;
		}
	}

	// public test(){
	// 	var list = [];
	//
	// 	var obs1 = new Observable((observer)=>{
	// 		this.logger.log('obs1');
	// 		var prom = new Promise((resolve, reject)=>{
	// 			setTimeout(resolve, 2000);
	// 		});
	// 		prom.then((data)=> {
	// 			this.logger.log('then obs1');
	// 			observer.next();
	// 			observer.complete();
	// 		})
	// 	});
	//
	// 	var obs2 = new Observable((observer)=>{
	// 		this.logger.log('obs2');
	// 		var prom = new Promise((resolve, reject)=>{
	// 			setTimeout(resolve, 2000);
	// 		});
	// 		prom.then((data)=> {
	// 			this.logger.log('then obs2');
	// 			observer.next();
	// 			observer.complete();
	// 		})
	// 	});
	//
	//
	// 	list.push(obs1, obs2);
	// 	this.logger.log('list', list);
	// 	var newObs = list.reduce((acc, obs)=>{
	// 		this.logger.log('reducing', acc, obs);
	// 		return acc.concat(obs);
	// 	})
	// 	this.logger.log('new obs', newObs);
	// 	newObs.subscribe((thing)=>{
	// 		this.logger.log('subscribing', thing);
	// 	})
	// 	// .complete(thing => this.logger.log('all done'));
	//
	// 	this.logger.log('done setup');
	//
	// }
	//
	// public test2(){
	// 	var list = [
	// 		()=>{
	// 			this.logger.log('obs1');
	// 			return new Promise<string>((resolve, reject)=>{
	// 				setTimeout(()=> resolve("resolve obs1"), 2000);
	// 			});
	// 		},
	// 		()=>{
	// 			this.logger.log('obs2');
	// 			return new Promise<string>((resolve, reject)=>{
	// 				setTimeout(()=> resolve("resolve obs2"), 2000);
	// 			});
	// 		},
	// 	];
	//
	// 	utils.sequencePopups(list).subscribe((thing)=>{
	// 		this.logger.log('subscribing', thing);
	// 	})
	// 	// .complete(thing => this.logger.log('all done'));
	//
	// 	this.logger.log('done setup');
	//
	// }
	//Promise<DragPromptOutput>
	public checkPromptConditions(
		analysis: DragAnalysis,
		droppedInFloating: boolean
	): Array<() => Promise<DragPromptOutput>> {
		var scopeSoup = this.planState.scopeSoup;
		var prompts = [];

		function wrap(type, payload) {
			return { type, payload };
		}
		if (analysis.isAlmostOutofBounds) {
			prompts.push(() => {
				let stringPiece = this.tickets.size > 1 ? 'these tickets' : 'this ticket';
				return scopeSoup
					.popup({
						title: 'Are you sure you want to drag ' + stringPiece + ' to that position?',
						description: 'Your drag position is outside of the expected position range for your plan.',
					})
					.then((payload) => wrap(DragPromptConditions.ALMOSTOUTOFBOUNDS, payload));
			});
			analyticsEventsService.positionViolationNagConfirm();
		}

		//temp floating space attachment blocker
		if (analysis.hasAttachments && droppedInFloating) {
			const prom = () =>
				analysis.hasAttachments
					.then((hasAttachments) => {
						if (hasAttachments) {
							return scopeSoup
								.popup({
									title: DRAG_ATTACHE_INTO_FLOATING,
									template: require('html-loader?root=src/!tpl/popups/simple.html'),
								})
								.then(() => Promise.reject());
						} else {
							return null;
						}
					})
					.then((val) => wrap(DragPromptConditions.HASATTACHMENTS, val));
			prompts.push(prom);
		}

		// If there are "preservedTicketId" conflicts, ask the user about resolving them:
		if (analysis.preservedIdConflicts) {
			prompts.push(() => {
				return scopeSoup
					.popup({
						template: `
						<h3>${strings.DRAG_DUPLICATE_TICKET_IDS_TITLE}</h3>
						<span class="description">${strings.DRAG_DUPLICATE_TICKET_IDS_DESCRIPTION}</span>
						<div class="button-wrapper">
							<a class="yes" ng-click="confirm()">${strings.DRAG_DUPLICATE_TICKET_IDS_OK}</a>
							<a class="no" ng-click="reject()">${strings.DRAG_DUPLICATE_TICKET_IDS_CANCEL}</a>
						</div>`,
					})
					.then((val) => wrap(DragPromptConditions.DUPLICATE_TICKET_IDS, val));
			});
		}

		return prompts;
	}

	private buildSnapCache(dragRect: Rectangle) {
		var cache = {
			xPull: new Set(),
			yPull: new Set(),
			yActive: new Set(),
		};
		this.planState.tickets.list$.pipe(first()).subscribe((fullTickets) => {
			fullTickets.forEach((ticket, id) => {
				if (this.tickets.has(id)) {
					return;
				}
				// this.logger.log('ticketactuls', ticket.view.isActualized);
				if (ticket.view.side === Side.ACTIVE) {
					cache.yActive.add(ticket.view.top); //same position
					cache.yActive.add(ticket.view.top + ticket.view.height + snappingDistance); //50px below the bottom
					cache.yActive.add(ticket.view.top - snappingDistance - dragRect.height); // bottom of drag rect 50px above
				} else {
					cache.xPull.add(ticket.view.left); //same position
					cache.xPull.add(ticket.view.left + ticket.view.width + snappingDistance); //50px to the right
					cache.xPull.add(ticket.view.left - snappingDistance - dragRect.width); // right of drag rect 50px to the left

					cache.yPull.add(ticket.view.top); //same position
					cache.yPull.add(ticket.view.top + ticket.view.height + snappingDistance); //50px below the bottom
					cache.yPull.add(ticket.view.top - snappingDistance - dragRect.height); // bottom of drag rect 50px above
				}
			});
		});
		return cache;
	}

	private findSnaps(num: number, snapCache) {
		// if(keyModifier.isAppleSafeCtrl){ return num; }
		var match = snapping(num, snapCache, window.stickiness, (listItem) => {
			return listItem;
		});
		return match ? match.loc : num;
	}

	//should probably do some fancy shit in here, do simple thing for now
	private ticketSelectionModfied(ticketSelection: TicketSelection) {
		//mid drag update
		if (this.tickets) {
			this.tickets = ticketSelection.list;
			this.recalcStart();
		}
		//initial
		else {
			this.tickets = ticketSelection.list;
		}
	}

	private generateActiveWriteData(calcedActive: PullCalculation, delta) {
		var calcedActiveWriteData = [];
		var snaps = this.buildSnapCache(calcedActive.activeRect);
		var y = this.findSnaps(calcedActive.activeRect.y, snaps.yActive) - this.startRect.y;
		// this.logger.log('calcedActive.startDate', calcedActive.startDate, );
		calcedActive.iterate((ticket, region) => {
			var work = ticket.view.lastWorkdayRep;
			var data = {
				left: Math.round(region.activeStartX + work.startOffsetDays * activeColumnWidth),
				top: Math.round(ticket.view.dragStartData.y + y),
				width: work.calendarDays * activeColumnWidth,
				height: activeColumnHeight,
				liveStart: calcedActive.startDate
					? moment(calcedActive.startDate).add(work.startOffsetDays, 'days').format('YYYY-MM-DD')
					: undefined,
				liveFinish: calcedActive.startDate
					? moment(calcedActive.startDate)
							.add(work.startOffsetDays + work.calendarDays - 1, 'days')
							.format('YYYY-MM-DD')
					: undefined,
				side: Side.ACTIVE,
			};
			calcedActiveWriteData.push({ write: data, $id: ticket.$id });
		});
		return calcedActiveWriteData;
	}

	private calculateActiveFromPullRect(tickets: Map<string, Ticket>, worldDelta: Point) {
		var leftEdge = this.startRect.x + worldDelta.x; //move it
		// this.logger.log('left edge', leftEdge);
		var activeColumns = this.angularLand.activeColumnService.columns();
		var match = snapping(leftEdge, activeColumns, activeColumnWidth, (listItem) => {
			return listItem.origStartX;
		});
		//snap it if there's somewhere to snap to
		if (match) {
			leftEdge = match.loc;
		}
		var dayOffset = 0; //increment as we walk through
		// var startDate = this.angularLand.activeColumnService.guessDateFromX(leftEdge + dayOffset * activeColumnWidth);
		var startDate = this.angularLand.activeColumnService.guessDateFromX(
			Math.min(this.planData.view.activeLineX, leftEdge),
			-activeColumnWidth
		);
		// this.logger.log('tickets', tickets);
		//don't love this "build pull data, then decorate with active data" thing, would prefer a more formal divide or a more formal decoration process
		var calculatedColumns = this.calculatePullColumns(tickets);

		var fullTop = Infinity;
		var fullBottom = -Infinity;

		//go in reverse
		for (var i = calculatedColumns.regions.length - 1; i >= 0; i--) {
			var r = calculatedColumns.regions[i];
			var columnWidth = 0;
			r.tickets.forEach((ticket) => {
				var rep = this.planState.calendarWorkdays.getWorkdayRepresentation(
					startDate,
					ticket.view.liveDurationDays,
					ticket.view.liveRole ? ticket.view.liveRole.$id : null,
					i === calculatedColumns.regions.length - 1
				);
				var size = rep.startOffsetDays + rep.calendarDays;
				if (size > columnWidth) {
					columnWidth = size;
				}
				if (ticket.view.dragStartData.y + worldDelta.y < fullTop) {
					fullTop = ticket.view.dragStartData.y + worldDelta.y;
				}
				if (ticket.view.dragStartData.y + activeColumnHeight + worldDelta.y > fullBottom) {
					fullBottom = ticket.view.dragStartData.y + activeColumnHeight + worldDelta.y;
				}
			});

			r.activeStartX = leftEdge + dayOffset * activeColumnWidth;
			r.activeFinishX = leftEdge + dayOffset * activeColumnWidth + columnWidth * activeColumnWidth;
			r.startDate = startDate;
			r.calculatedCalendarDays = columnWidth;
			dayOffset += columnWidth;
			//advance the start date
			startDate = this.angularLand.activeColumnService.guessDateFromX(
				Math.min(this.planData.view.activeLineX, leftEdge + dayOffset * activeColumnWidth),
				-activeColumnWidth
			);
			// startDate = this.angularLand.activeColumnService.guessDateFromX(leftEdge + dayOffset * activeColumnWidth);
		}
		var fullLeft = calculatedColumns.regions[calculatedColumns.regions.length - 1].activeStartX;
		var fullRight = calculatedColumns.regions[0].activeFinishX;
		//TODO - get y values
		calculatedColumns.activeRect = new Rectangle(
			fullLeft,
			fullTop,
			fullRight - fullLeft,
			fullBottom - fullTop
		);

		calculatedColumns.iterate((ticket, r, ticketIdx, i) => {
			var r = calculatedColumns.regions[i];
			r.tickets.forEach((ticket) => {
				var rightened = this.righten(
					r.startDate,
					r.calculatedCalendarDays,
					ticket,
					i === calculatedColumns.regions.length - 1
				);
				ticket.view.lastWorkdayRep = rightened;
			});
		});

		// logger.log('calculatedColumns', calculatedColumns.regions[0].calculatedCalendarDays);
		// logger.log('calculatedColumns', calculatedColumns.regions.map(c => c.calculatedCalendarDays));
		return calculatedColumns;
	}

	private calculatePullFromActiveRect(tickets: Map<string, Ticket>) {
		// logger.log('tickets', tickets);
		//don't love this "build pull data, then decorate with active data" thing, would prefer a more formal divide or a more formal decoration process
		var calculatedColumns = this.calculatePullColumns(tickets);
		calculatedColumns.regions.forEach((region, idx, list) => {
			var reversedIdx = list.length - idx - 1;
			region.startX = (PLAN_CONSTANTS.PULL_TICKET_WIDTH + snappingDistance) * reversedIdx;
			region.finishX = PLAN_CONSTANTS.PULL_TICKET_WIDTH * (reversedIdx + 1);
		});
		// calculatedColumns.pullStartRect.x = calculatedColumns.regions[0].startX;
		// calculatedColumns.pullStartRect.width = calculatedColumns.regions[calculatedColumns.regions.length-1].finishX - calculatedColumns.regions[0].startX;
		calculatedColumns.pullStartRect.x =
			calculatedColumns.regions[calculatedColumns.regions.length - 1].startX;
		calculatedColumns.pullStartRect.width =
			calculatedColumns.regions[0].finishX -
			calculatedColumns.regions[calculatedColumns.regions.length - 1].startX;
		// logger.log('pullStartRect',calculatedColumns.regions, calculatedColumns.pullStartRect);
		return calculatedColumns;
	}

	//used to position tickets in the activeFromPullRect within a column as far right as they can go (perf challenged)
	//naive, start from left, keep retrying using getWorkDayRepresentation, also includes some of it's own date math
	private righten(startDate, maxDays, ticket, isFirst) {
		var rep = this.planState.calendarWorkdays.getWorkdayRepresentation(
			startDate,
			ticket.view.liveDurationDays,
			ticket.view.liveRole ? ticket.view.liveRole.$id : null,
			isFirst
		);
		var dayOffset = 0;
		var lastRep = null;
		while (true) {
			if (rep.startOffsetDays + rep.calendarDays + dayOffset === maxDays) {
				return decorateRep(rep, dayOffset);
			} else if (rep.startOffsetDays + rep.calendarDays + dayOffset > maxDays) {
				return decorateRep(lastRep, dayOffset);
			} //this can't be hit first iteration
			else {
				lastRep = rep;
				var nextDay = moment(startDate, 'YYYY-MM-DD')
					.add(1 + dayOffset, 'days')
					.format('YYYY-MM-DD');
				dayOffset++;
				rep = this.planState.calendarWorkdays.getWorkdayRepresentation(
					nextDay,
					ticket.view.liveDurationDays,
					ticket.view.liveRole ? ticket.view.liveRole.$id : null,
					isFirst
				);
			}
		}

		function decorateRep(rep, offset) {
			rep.startOffsetDays += offset;
			return rep;
		}
	}

	private figureOutActive(rect: Rectangle, tickets: Ticket[]) {
		var leftSide = Math.round(Math.min(rect.left, this.planData.view.activeLineX - activeColumnWidth));
		if (leftSide > this.planData.view.activeLineX - activeColumnWidth) {
			leftSide = this.planData.view.activeLineX - activeColumnWidth;
		}
		var rightSide;

		var startGhosting = leftSide >= this.planData.view.activeLineX - activeColumnWidth;

		var allDone = false;

		while (!allDone) {
			var leftDate = this.angularLand.activeColumnService.guessDateFromX(leftSide, -activeColumnWidth);
			rightSide = -Infinity;
			allDone = true;

			// debugger;
			innerLoop: for (var i = 0; i < tickets.length; i++) {
				var ticket = tickets[i];
				var ticketStartData = ticket.view.dragStartData;

				var workDayStartOffset = this.planState.calendarWorkdays.getCalendarOffset(
					leftDate,
					ticketStartData.startWorkDayOffset,
					ticket.view.liveRole ? ticket.view.liveRole.$id : null,
					true
				);
				var altStartOffset = workDayStartOffset.calendarDays; // + workDayStartOffset.startOffsetDays;

				// logger.log('ticket', ticket.view.activityName, ticketStartData.startWorkDayOffset, 'work cal', workDayStartOffset);
				var ticketLeft = leftSide + altStartOffset * activeColumnWidth;

				var startDate = this.angularLand.activeColumnService.guessDateFromX(ticketLeft, -activeColumnWidth);
				//logger.log('ticketStartDate', startDate);
				var work = this.planState.calendarWorkdays.getWorkdayRepresentation(
					startDate,
					ticket.view.liveDurationDays,
					ticket.view.liveRole ? ticket.view.liveRole.$id : null,
					ticketStartData.startDayOffset === 0
				);
				(<any>work).shiftedStartOffset = altStartOffset;
				// work.startOffsetDays += (altStartOffset - ticketStartData.startDayOffset);
				//no workdays left problem, pull everything back a day and try again
				if (ticketLeft + work.startOffsetDays * activeColumnWidth >= this.planData.view.activeLineX) {
					// logger.log('no workdays left');
					leftSide -= activeColumnWidth;
					allDone = false;
					break innerLoop;
				}

				ticket.view.lastWorkdayRep = work;

				// logger.log('ticketLeft', ticketLeft, this.planData.activeLineX - activeColumnWidth);
				if (ticketLeft >= this.planData.view.activeLineX - activeColumnWidth) {
					startGhosting = true;
				}
				// logger.log('ticketLeft', ticketLeft)
				var ticketRight =
					ticketLeft + work.startOffsetDays * activeColumnWidth + work.calendarDays * activeColumnWidth;
				if (ticketRight > rightSide) {
					rightSide = ticketRight;
				}
			}
		}

		rect.x = leftSide;
		rect.width = rightSide - leftSide;

		// logger.log('output rect', rect.left, rect.right);
		return startGhosting;
	}

	private showGhostTickets() {
		if (this.internalGhostTickets.size) {
			return;
		} //already shown
		this.tickets.forEach((t) => {
			var ghost = {
				$id: 'ghost-' + t.$id,
				realId: t.$id,
				isGhost: true,
				view: Object.assign({}, t.view),
			};
			this.internalGhostTickets.set(t.$id, ghost);
			this.ghostTickets.next(new FancyTicketAction(ghost, FancyActionTypes.childAdded, ghost.$id));
		});
	}
	private updateGhostTickets() {}
	private hideGhostTickets() {
		this.internalGhostTickets.forEach((t) => {
			this.ghostTickets.next(new FancyTicketAction(null, FancyActionTypes.childRemoved, t.$id));
		});

		this.internalGhostTickets.clear();
	}

	private calculatePullColumns(tickets: Map<string, Ticket>) {
		return new PullCalculation(tickets);
	}

	private dragAtEdgeListener(map) {}

	private calcStartData(center: Point, tickets: Map<string, Ticket>) {
		var camera = this.planState.canvasConnector.camera;
		var z = camera.getScale().x;

		var realX = camera.screenToRealX(center.x);

		// MOC-4282, origin side heuristic. There is a slim case where if you start a drag extremely close to the active line the first event
		// from hammer can indicate the wrong side of the line. We have no good way of actually knowing for sure. So as an added check
		// if all the tickets in the selection are on the same side, we'll use that as the origin side. If not, we'll fall back to the gesture.
		// This takes an extremely unlikely thing, and make it much more unlikely. Especially since a mixed selection can only drag up and down.
		let ticketOriginSide: Side;
		tickets.forEach((t) => {
			if (!ticketOriginSide && t.view.side) {
				ticketOriginSide = t.view.side;
			} else if (ticketOriginSide !== t.view.side) {
				ticketOriginSide = Side.UNKNOWN;
			}
		});
		const gestureOriginSide =
			this.planData.view.activeLineX === undefined ||
			this.planData.view.activeLineX === null ||
			realX > this.planData.view.activeLineX
				? Side.PULL
				: Side.ACTIVE;
		const originSide =
			ticketOriginSide && ticketOriginSide !== Side.UNKNOWN ? ticketOriginSide : gestureOriginSide;

		return {
			screenX: center.x,
			screenY: center.y,
			x: realX,
			y: camera.screenToRealY(center.y),
			z: camera.getScale().x,
			originSide,
			hasPins: false,
			hasLoggedPullPinWarning: false,
		};
	}

	public updateDragAtEdgeDeadZones(zones: Rectangle[]) {
		this.dragAtEdge.deadZones = zones;
	}

	public applyVarianceFromPrompt(tickets: Map<string, Ticket>, variance) {
		if (!variance || !tickets) {
			return;
		}
		// debugger;
		tickets.forEach((t) => {
			if (t.view.needsVariance) {
				let newPin = applyVarianceToPin(
					t.view.liveLastPromise,
					variance.varianceId,
					variance.varianceComment
				);
				this.planState.actions.updateTicketLiveData(t.$id, { liveLastPromise: newPin });
			}
		});
		this.logger.log('apply variance', variance);
		this.showVariancePrompt.next(false);
		this.saveTickets(tickets);
	}
	public cancelVariancePrompt(tickets: Map<string, Ticket>) {
		this.logger.log('cancel variance');
		this.planState.actions.changeTicketViewToDefault(tickets);
		this.showVariancePrompt.next(false);
		this.dragCleanUp();
	}
}

//move to a utility file:

function checkForConstraintAcceptance(you: ProjectMember, tickets: ForEachAble<Ticket>) {
	let constraintsToBeAccepted: Ticket[] = [];
	let pmYou = you.$id;
	tickets.forEach((ticket) => {
		if (
			ticket.view.type === 'constraint' &&
			ticket.view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ASSIGNED &&
			ticket.view.assignedProjectMemberId &&
			pmYou === ticket.view.assignedProjectMemberId
		) {
			constraintsToBeAccepted.push(ticket);
		}
	});
	return constraintsToBeAccepted;
}

function sortActiveTickets(tickets) {
	return sortTicketMap(
		tickets,
		(ticketA, ticketB) =>
			ticketA.view.dragStartData.startDayOffset < ticketB.view.dragStartData.startDayOffset
	);
}

//start lazy
function sortTicketMap(tickets: Map<string, Ticket>, cb) {
	var arr = [...tickets.values()];
	arr.sort(cb);
	return arr;
}

//perf might not be great here
// function calculateStartRect(tickets:Map<string, Ticket>){
// 	var rect;
// 	var lefts = [];
// 	var tops = [];
// 	tickets.forEach((t)=>{
// 		lefts.push(t.view.left);
// 		tops.push(t.view.top);
// 	});
// 	var maxX = Math.max.apply(Math, lefts) + PLAN_CONSTANTS.PULL_TICKET_WIDTH;
// 	var minX = Math.min.apply(Math, lefts);
// 	var maxY = Math.max.apply(Math, tops) + PLAN_CONSTANTS.PULL_TICKET_HEIGHT;
// 	var minY = Math.min.apply(Math, tops);
//
// 	return new Rectangle(minX, minY, maxX - minX, maxY - minY);
// }
export function calculateStartRect(tickets: ListLike<Ticket>) {
	var rect;
	var lefts = [];
	var rights = [];
	var tops = [];
	var bottoms = [];
	tickets.forEach((t) => {
		// lefts.push(t.view.left);
		// rights.push(t.view.left + t.view.width);
		// tops.push(t.view.top);
		// bottoms.push(t.view.top + t.view.height);
		lefts.push(t.view.dragStartData.x);
		rights.push(t.view.dragStartData.x + t.view.dragStartData.width);
		tops.push(t.view.dragStartData.y);
		bottoms.push(t.view.dragStartData.y + t.view.dragStartData.height);
	});
	var maxX = Math.max.apply(Math, rights);
	var minX = Math.min.apply(Math, lefts);
	var maxY = Math.max.apply(Math, bottoms);
	var minY = Math.min.apply(Math, tops);

	return new Rectangle(minX, minY, maxX - minX, maxY - minY);
}
